
import React from 'react';
import { Award, ExternalLink, Calendar, CheckCircle } from 'lucide-react';
import { AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Certification } from '@/data/certifications';

interface CertificationCardProps {
  certification: Certification;
  index: number;
  isVisible: boolean;
}

const CertificationCard: React.FC<CertificationCardProps> = ({ certification, index, isVisible }) => {
  return (
    <AccordionItem 
      key={certification.id} 
      value={certification.id}
      className={`bg-card/40 backdrop-blur-sm border border-primary/10 rounded-lg mb-4 overflow-hidden transition-all ${
        isVisible 
          ? 'opacity-100 translate-y-0 animate-fade-in' 
          : 'opacity-0 translate-y-8'
      }`}
      style={{ animationDelay: `${index * 150}ms` }}
    >
      <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-accent/10 group">
        <div className="flex items-start justify-between gap-3 text-left w-full">
          <div className="flex items-start gap-3">
            <Award className="h-5 w-5 text-primary shrink-0 mt-1" />
            <div>
              <h3 className="font-medium text-lg">{certification.name}</h3>
              <p className="text-sm text-muted-foreground">{certification.issuer}</p>
            </div>
          </div>
          {certification.featured && (
            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
              Featured
            </Badge>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-6 pb-5 pt-2">
        <div className="ml-8 border-l-2 border-primary/20 pl-5 py-1">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Calendar className="h-4 w-4" />
            <span>Issued: {certification.date}</span>
            {certification.expiry && (
              <span>• Expires: {certification.expiry}</span>
            )}
          </div>
          
          <p className="mb-4 text-sm">{certification.description}</p>
          
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Skills</h4>
            <div className="flex flex-wrap gap-2">
              {certification.skills.map((skill, idx) => (
                <span 
                  key={idx} 
                  className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>{certification.credentialId ? `Credential ID: ${certification.credentialId}` : "Verified Achievement"}</span>
            </div>
            
            {certification.credentialURL && (
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/80 hover:bg-primary/10 p-1 h-auto"
                asChild
              >
                <a 
                  href={certification.credentialURL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1"
                >
                  View Certificate <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default CertificationCard;
